import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { nanoid } from 'nanoid';
import {
  multipartUploadParts,
  multipartUploadPartsUploadParams,
  multipartUploadAbort,
  multipartUploadComplete
} from '@api/MultipartUpload';
import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';

export function createMVCommentAttachmentsUppy(
  assetId: string,
  instanceScope?: string
) {
  let sessionId = sessionStorage.getItem(
    'mv-comment-attachments-uppy-session-id'
  );
  if (!sessionId) {
    sessionId = nanoid();
    sessionStorage.setItem('mv-comment-attachments-uppy-session-id', sessionId);
  }
  const uppy = new Uppy({
    id: ['mv-comment-attachments-uppy', assetId, instanceScope, sessionId]
      .filter(Boolean)
      .join('_'),
    restrictions: {
      requiredMetaFields: ['readyForUpload']
    } as any
  });
  uppy.use(AwsS3, {
    shouldUseMultipart: true,
    getChunkSize() {
      const MB = 1024 * 1024;
      return 5 * MB;
    },
    async createMultipartUpload(file) {
      const commentId = file.meta.commentId as string;
      const {
        data: { attachmentId, uploadId, key }
      } = await apiClient.attachment.attachmentControllerInitAttachmentUpload({
        targetId: commentId,
        type: 'av-comment-attachment',
        fileName: nonNull(file.name),
        fileSize: nonNull(file.size)
      });
      file.meta.attachmentId = attachmentId;
      uppy.setFileMeta(file.id, { attachmentId });
      return { uploadId, key };
    },
    async listParts(file, { key, uploadId }) {
      const {
        data: { parts }
      } = await multipartUploadParts({ key, uploadId: nonNull(uploadId) });
      return parts;
    },
    async signPart(file, { key, uploadId, partNumber }) {
      try {
        const {
          data: { presignedUrls, headers }
        } = await multipartUploadPartsUploadParams({
          key,
          uploadId,
          partNumber: [partNumber]
        });
        return { url: presignedUrls[partNumber], headers };
      } catch (err) {
        console.warn(err, '-> prepareUploadParts');
        throw err;
      }
    },
    async abortMultipartUpload(file, { key, uploadId }) {
      await multipartUploadAbort({ key, uploadId: nonNull(uploadId) });
    },
    async completeMultipartUpload(file, { key, uploadId, parts }) {
      const { data } = await multipartUploadComplete({
        key,
        uploadId,
        parts: parts.map((part) => ({
          PartNumber: nonNull(part.PartNumber),
          ETag: nonNull(part.ETag)
        }))
      });
      return data;
    }
  });
  return {
    uppy,
    assetId
  };
}
